import { Component } from './component-base.js';

const DEFAULT_SCROLL_THRESHOLD = 100;

/** When a `shrink-on-scroll` hero exists: header treats scroll as "past hero" once `getBoundingClientRect().bottom` is ≤ this (px). Lower = solid header / `.scrolled` later. */
const HERO_BANNER_BOTTOM_EXIT_PX = 100;

/** Narrow viewports: delay solid header until the hero bottom clears the top edge (iOS has no rubber-band; smaller value avoids white bar while the shrink animation still reads as "on hero"). */
const HERO_BANNER_BOTTOM_EXIT_PX_MOBILE = 100;

/**
 * StickyHeader
 * Handles sticky header behavior with scroll detection and dynamic show/hide
 */
class StickyHeader extends Component {
  #shrinkBannerResizeObserver = null;
  #shrinkBanner = null;

  constructor() {
    super();
    this.scrollTolerance = 5;
    this.lastScrollY = 0;
    this.currentScrollY = 0;
    this.isScrolled = false;
    this.isHidden = false;
    this.headerElement = null;
    this.headerType = 'none';
    this.scrollTimeout = null;
    this.throttleDelay = 10;
    this.toolbarElement = null;
    this.toolbarHeight = 0;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    this.headerElement = this.closest('header');
    if (!this.headerElement) {
      console.error('[StickyHeader] No parent <header> element found');
      return;
    }

    this.headerType = this.headerElement.dataset.headerType || 'none';

    if (this.headerType === 'none') {
      return;
    }

    this.currentScrollY = window.scrollY;
    this.lastScrollY = this.currentScrollY;

    // Check for sticky toolbar and calculate offset
    this.#calculateToolbarOffset();

    this.#bindShrinkBannerWatch();

    this.#addEventListeners();

    if (!this.#isHeroBannerOffViewport() && this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }

    this.#updateHeaderState();

    // Scroll restoration, font/layout, and toolbar offset can settle after first paint.
    // Without DevTools the page is faster; connectedCallback can run before the final scroll position is applied.
    this.#scheduleScrollResync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    this.#shrinkBannerResizeObserver?.disconnect();
    this.#shrinkBannerResizeObserver = null;
    this.#shrinkBanner = null;
    
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  #bindShrinkBannerWatch() {
    const shrink = document.querySelector('shrink-on-scroll');
    this.#shrinkBanner = shrink || null;
    if (!shrink) {
      this.#shrinkBannerResizeObserver?.disconnect();
      this.#shrinkBannerResizeObserver = null;
      return;
    }

    const onBannerLayoutChange = () => {
      this.#updateHeaderState();
    };

    if (!this.#shrinkBannerResizeObserver) {
      this.#shrinkBannerResizeObserver = new ResizeObserver(() => onBannerLayoutChange());
      this.#shrinkBannerResizeObserver.observe(shrink);
    }
  }

  #heroBannerExitThresholdPx() {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      return HERO_BANNER_BOTTOM_EXIT_PX_MOBILE;
    }
    return HERO_BANNER_BOTTOM_EXIT_PX;
  }

  // True when shrink hero box is fully above the viewport (hide-on-scroll only after that).
  #isHeroBannerOffViewport() {
    if (!this.#shrinkBanner) {
      return this.currentScrollY > DEFAULT_SCROLL_THRESHOLD;
    }
    return this.#shrinkBanner.getBoundingClientRect().bottom <= this.#heroBannerExitThresholdPx();
  }

  #addEventListeners() {
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    
    // Add hover listeners if remove transparency on hover is enabled
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.addEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.addEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #removeEventListeners() {
    window.removeEventListener('scroll', this.#handleScroll);
    
    if (this.headerElement.hasAttribute('data-remove-transparency-on-hover')) {
      this.headerElement.removeEventListener('mouseenter', this.#handleMouseEnter);
      this.headerElement.removeEventListener('mouseleave', this.#handleMouseLeave);
    }
  }

  #handleMouseEnter = () => {
    this.headerElement.classList.add('header-hovered');
  };

  #handleMouseLeave = () => {
    this.headerElement.classList.remove('header-hovered');
  };

  #handleScroll = () => {
    if (this.scrollTimeout) return;

    this.scrollTimeout = setTimeout(() => {
      this.currentScrollY = window.scrollY;
      this.#updateHeaderState();
      this.scrollTimeout = null;
    }, this.throttleDelay);
  };

  /**
   * Re-read window scroll after layout / history restoration so header state matches reality.
   * Resets scroll delta baseline so scroll-up logic is not triggered by a one-off correction.
   */
  #syncScrollFromWindow() {
    if (this.headerType === 'none') return;
    const y = window.scrollY;
    this.currentScrollY = y;
    this.lastScrollY = y;
    this.#updateHeaderState();
  }

  #scheduleScrollResync() {
    if (this.headerType === 'none') return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#bindShrinkBannerWatch();
        this.#syncScrollFromWindow();
      });
    });

    window.addEventListener(
      'load',
      () => {
        this.#bindShrinkBannerWatch();
        this.#syncScrollFromWindow();
      },
      { once: true }
    );
  }

  #updateHeaderState() {
    const scrollDelta = this.currentScrollY - this.lastScrollY;
    
    if (this.headerElement.hasAttribute('data-header-hidden')) {
      this.headerElement.removeAttribute('data-header-hidden');
      this.headerElement.style.transition = '';
    }
    
    this.#updateScrolledClass();
    
    if (this.headerType === 'scroll-up') {
      this.#updateScrollUpBehavior(scrollDelta);
    }
    
    this.lastScrollY = this.currentScrollY;
  }

  #updateScrolledClass() {
    const shouldBeScrolled = this.#isHeroBannerOffViewport();
    
    if (shouldBeScrolled !== this.isScrolled) {
      this.isScrolled = shouldBeScrolled;
      
      if (this.isScrolled) {
        this.headerElement.classList.add('scrolled');
      } else {
        this.headerElement.classList.remove('scrolled');
      }
    }
  }

  #updateScrollUpBehavior(scrollDelta) {
    if (!this.#isHeroBannerOffViewport()) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (this.headerElement.classList.contains('menu-open')) {
      if (this.isHidden) {
        this.#showHeader();
      }
      return;
    }
    
    if (scrollDelta > this.scrollTolerance) {
      if (!this.isHidden) {
        this.#hideHeader();
      }
    } else if (scrollDelta < -this.scrollTolerance) {
      if (this.isHidden) {
        this.#showHeader();
      }
    }
  }

  #hideHeader() {
    this.isHidden = true;
    this.headerElement.classList.add('header-hidden');
  }

  #showHeader() {
    this.isHidden = false;
    this.headerElement.classList.remove('header-hidden');
  }

  #calculateToolbarOffset() {
    // Find sticky toolbar (could be above or below header in header group)
    this.toolbarElement = document.querySelector('.toolbar-section');
    const headerGroupWrapper = this.headerElement.closest('.shopify-section-group-header-group');
    if (!this.toolbarElement) {
      this.toolbarHeight = 0;
      if (headerGroupWrapper) headerGroupWrapper.style.top = '0px';
      return;
    }


    // Check if toolbar has sticky positioning
    const toolbarStyles = window.getComputedStyle(this.toolbarElement);
    const isSticky = toolbarStyles.position === 'sticky' || toolbarStyles.position === 'fixed';
    
    if (!isSticky) {
      this.toolbarHeight = 0;
      if (headerGroupWrapper) headerGroupWrapper.style.top = '0px';
      return;
    }

    // Check if toolbar is above the header
    if (!headerGroupWrapper) {
      this.toolbarHeight = 0;
      return;
    }

    const toolbarRect = this.toolbarElement.getBoundingClientRect();
    const headerRect = headerGroupWrapper.getBoundingClientRect();
    const isToolbarAboveHeader = toolbarRect.top < headerRect.top;
    
    if (isToolbarAboveHeader) {
      // Toolbar is above header, set header top offset
      this.toolbarHeight = this.toolbarElement.offsetHeight;
      headerGroupWrapper.style.top = `${this.toolbarHeight}px`;
    } else {
      // Toolbar is below header, no offset needed
      this.toolbarHeight = 0;
      headerGroupWrapper.style.top = '0px';
    }
  }
}

function initStickyHeader() {
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', StickyHeader);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyHeader);
} else {
  initStickyHeader();
}

export { StickyHeader, initStickyHeader };